import { CURRENT_YEAR, IS_AEP_SELECTION_WINDOW, IS_POST_AEP_LOCK_NEXT_YEAR_WINDOW } from './config';
import { makeCustomEvent, EVENT } from './events';
import { getIshopForm, ISHOP_FORM_ID } from './form';
import { BackendFormResponse } from './api';

export enum PRODUCT_TYPE {
    PPO = 'PPO',
    EPO = 'EPO',
    HMO = 'HMO',
    POS = 'POS',
}

export enum LOB {
    IADV = 'IADV',
    ILOW = 'ILOW',
    ICLT = 'ICLT',
    BHOM = 'BHOM',
    BHNV = 'BHNV',
    BCAR = 'BCAR',
    ICON = 'ICON',
}

export const networkFilterOrder: LOB[] = [LOB.IADV, LOB.BCAR, LOB.ILOW, LOB.BHOM, LOB.ICLT];

export const lobNetworkLabel = new Map<LOB, string>([
    [LOB.IADV, 'Blue Advantage - Broad'],
    [LOB.ILOW, 'Blue Value - Limited'],
    [LOB.ICLT, 'Blue Local - Local'],
    [LOB.BHOM, 'Blue Home - Local'],
    [LOB.BHNV, 'Blue Home - Local'],
    [LOB.BCAR, 'Blue Care - Broad'],
]);

export enum METALLIC_LEVEL {
    GOLD = 'Gold',
    SILVER = 'Silver',
    BRONZE = 'Bronze',
}

export const metallicSortOrder: METALLIC_LEVEL[] = [METALLIC_LEVEL.GOLD, METALLIC_LEVEL.SILVER, METALLIC_LEVEL.BRONZE];

export enum PRICING {
    EASY = 'EASY',
    NONE = 'NONE',
}

export interface BackendPlanRate {
    applicantIndex: string;
    applicantRate: number;
}

export interface BackendPlan {
    familyOutOfPocketMax: string;
    planVariant: string;
    maternity: string;
    planNetwork: string;
    logo: string;
    planId: string;
    familyMemberOutOfPocketMax: string;
    hsaEligible: string;
    marketplace: string;
    planAdministrationRating: number | string;
    metal: string;
    planRank: string;
    coinsuranceAmt: string;
    heatmapNetwork: string;
    subheader: string;
    facetsRxId: string;
    familyDeductible: string;
    specialist: string;
    subsidyEligible: string;
    name: string;
    header: string;
    pdfName: string;
    medicalCareRating: number | string;
    individualDeductibleAmt: string;
    urgentCare: string;
    facetsHsaId?: string;
    rxGeneric: string;
    rxBrandPreferred: string;
    individualOutOfPocketMax: string;
    marketingName: string;
    memberExperienceRating: number | string;
    rxGenericNonPreferred: string;
    overallQualityRating: number | string;
    prescriptionDrugs: string;
    familyMemberDeductible: string;
    productYear: string;
    telehealth: string;
    emergencyRoom: string;
    lobCode: string;
    productType: string;
    QHPID?: string;
    planType: string;
    serviceArea: string;
    rxSpecialty: string;
    discontinued: string | boolean;
    planCode: string;
    individualDeductible: string;
    maternityPlanId: string;
    primaryCare: string;
    rxTier6: string;
    facetsPedPlanCode: string;
    facetsMedId?: string;
    coinsuranceText: string;
    rxDeductible: string;
    rxBrandNonPreferred: string;
    pricing: string;
    lob: string;
    productLineCode: string;
    allowSubsidy: boolean;
    deductible: string;
    planPopularityIndex: number;
    bcbsncRecommendedPlan?: boolean;
    agentRecommendedPlan?: boolean;
    metalType: string;
    network: string;
    coinsurance: string;
    rates: BackendPlanRate[];
    totalRate: number;
    bolCode: string;
    qhpId: string;
    totalIndividualResponsibilityAmt: number;
    pickOrder?: number;
    // New
    producerRecommended?: boolean;
    // added on
    primaryCareInn?: string;
    primaryCareOon?: string;
    specialistCareInn?: string;
    specialistCareOon?: string;
    tier1MedInn?: string;
    tier1MedOnn?: string;
    tier2MedInn?: string;
    tier2MedOnn?: string;
    tier3MedInn?: string;
    tier3MedOnn?: string;
    tier4MedInn?: string;
    tier4MedOnn?: string;
    tier5MedInn?: string;
    tier5MedOnn?: string;
    xRaysandDInn?: string;
    xRaysandDOon?: string;
    imagingTestsIn?: string;
    imagingTestsOon?: string;
    ambulanceInn?: string;
    ambulanceOOn?: string;
    hospitalStayFInn?: string;
    hospitalStayFOon?: string;
    hospitalStayPInn?: string;
    hospitalStayPOon?: string;
    outProFInn?: string;
    outProFOon?: string;
    outProDInn?: string;
    outProDOon?: string;
    physicalTherapyInn?: string;
    physicalTherapyOon?: string;
    babyWellnessInn?: string;
    babyWellnessOon?: string;
    laborDeliverInn?: string;
    laborDeliverOon?: string;
    emergencyRoomGeneric?: string;
    urgentCareInn?: string;
    urgentCareOon?: string;
    preventiveCareInn?: string;
    preventiveCareOon?: string;
    coinsuranceInn?: string;
    coinsuranceOon?: string;
    outOfPocketMaxIndividualInn?: string;
    outOfPocketMaxIndividualOon?: string;
    outOfPocketMaxFamilyMemberInn?: string;
    outOfPocketMaxFamilyMemberOon?: string;
    outOfPocketMaxFamilyInn?: string;
    outOfPocketMaxFamilyOon?: string;
    individualDeductibleInn?: string;
    individualDeductibleOon?: string;
    familyMemberDeductibleInn?: string;
    familyMemberDeductibleOon?: string;
    familyDeductibleInn?: string;
    familyDeductibleOon?: string;
    facetsProductId?: string;
    notSelectable?: boolean;
    showSubsidy?: boolean;
    producerSelected?: boolean;
    bestSeller?: boolean;
    genderAffirmC?: boolean | string;
    // from new query
    applicantRate?: {
        ageAsOfEffectiveDate: number;
        maternityRiderIndicator: boolean;
        maternityRiderPremium: number;
        medicalEnrollmentPlan: string;
        memberExcludedFromBillingIndicator: boolean;
        monthlyPremium: number;
        overAgeDependentIndicator: boolean;
        personId: string;
    }[];
    planRate?: {
        firstMonthPremium: number;
        firstMonthResponsibility: number;
        maternityRiderPremium: number;
        monthlyPremium: number;
        monthlyResponsibility: number;
    };
    bluecrossncPartNumber?: string;
}

/**coinsuranceInn
coinsuranceOon
outOfPocketMaxIndividualInn
outOfPocketMaxIndividualOon */
export interface IssApp {
    enrollmentType: string;
    referralSource: string;
    eligibilityCodes: { eligibilityCodeKey: 'aep' | 'sep' }[];
    formVersion: string;
    planInformation: {
        selectedPlanName: string;
        selectedProductFamily: string;
        monthlyPremium: number;
        selectedPlanId: string;
        requestedEffectiveDate: string;
    };
    physicalAddress: {
        county: string;
        zipcode: string;
        state: string;
    };
    applicants: {
        isPrimary: boolean;
        dateOfBirth: string;
        relationshipToPrimary: string;
        applicantId: string;
    }[];
    quoteId?: string;
    agentInformation?: {
        agentNPN?: string;
        agentFullName?: string;
        agentPhone?: string;
        agencyInternalNumber?: string;
        agencyNPN?: string;
        agentInternalNumber?: string;
    };
}

export interface SelectPlan {
    newApplication: 'Y' | 'N';
    issApp: IssApp;
}

function getEffectiveYear(effectiveStartDate?: string): string | undefined {
    if (!effectiveStartDate) return undefined;

    // Supports MM/DD/YYYY, YYYY-MM-DD, and YYYY.
    const slashDateMatch = effectiveStartDate.match(/^\d{2}\/\d{2}\/(\d{4})$/);
    if (slashDateMatch) return slashDateMatch[1];

    const isoDateMatch = effectiveStartDate.match(/^(\d{4})-\d{2}-\d{2}$/);
    if (isoDateMatch) return isoDateMatch[1];

    const yearMatch = effectiveStartDate.match(/^(\d{4})$/);
    if (yearMatch) return yearMatch[1];

    return undefined;
}

export function shouldUseLegacyLinks(effectiveStartDate?: string): boolean {
    const effectiveYear = getEffectiveYear(effectiveStartDate);

    // During AEP, honor the selected effective plan year from the question selection.
    if (IS_AEP_SELECTION_WINDOW && effectiveYear) {
        return effectiveYear === '2026';
    }

    // Outside AEP, preserve existing behavior.
    return CURRENT_YEAR === '2026' && !IS_POST_AEP_LOCK_NEXT_YEAR_WINDOW;
}

/**
 * Generates a provider link based on the LOB code.
 * @param lobCode - The LOB code for the plan.
 * @returns The provider link URL or `nothing` if the LOB code is invalid.
 */
export function getProviderLink(lobCode: string): string {
    const networkIdMap: { [key: string]: number } = {
        [LOB.IADV]: 3,
        [LOB.ILOW]: 28,
        [LOB.ICLT]: 9,
        [LOB.BHOM]: 25,
        [LOB.BHNV]: 32,
        [LOB.BCAR]: 6,
        [LOB.ICON]: 38,
    };

    const networkId = networkIdMap[lobCode];
    if (networkId) {
        return `https://healthnav.bcbsnc.com/?ci=COMMERCIAL&network_id=${networkId}`;
    }
    return '';
}

export const networkDescriptions: Record<string, string> = {
    [LOB.IADV]: 'Available in all 100 counties in North Carolina off the Marketplace.',
    [LOB.BCAR]:
        'NOT available in the following counties: Alamance, Anson, Cabarrus, Caswell, Chatham, Cleveland, Davidson, Davie, Durham, Forsyth, Franklin, Gaston, Guilford, Johnston, Lee, Lincoln, Mecklenburg, Nash, Orange, Person, Randolph, Rowan, Stanly, Stokes, Union, Wake, Wilkes, and Yadkin.',
    [LOB.BHNV]:
        'Limited to the following counties: Cabarrus, Davidson, Forsyth, Mecklenburg, Rowan, Stokes, Union, and Yadkin.',
    [LOB.BHOM]:
        'Limited to the following counties: Alamance, Caswell, Chatham, Durham, Franklin, Johnston, Lee, Nash, Orange, Person, and Wake.',
    [LOB.ICLT]:
        'Limited to the following counties: Anson, Cabarrus, Cleveland, Davidson, Davie, Forsyth, Gaston, Guilford, Lincoln, Mecklenburg, Randolph, Rowan, Stanly, Stokes, Union, Wilkes, and Yadkin.',
    [LOB.ILOW]:
        'Limited to the following counties: Alexander, Brunswick, Burke, Caldwell, Catawba, Cleveland, Columbus, Duplin, Gaston, Iredell, Lincoln, New Hanover, Onslow, Pender, and Surry.',
};

export const networkDescriptions2026: Record<string, string> = {
    [LOB.ICON]: 'Limited to the following counties: Guilford, Randolph, and Rockingham.',
    [LOB.IADV]: 'Available in all 100 counties in North Carolina off the Marketplace.',
    [LOB.BCAR]:
        'NOT available in the following counties: Alamance, Caswell, Chatham, Durham, Franklin, Johnston, Lee, Orange, Person, and Wake.',
    [LOB.BHNV]:
        'Limited to the following counties: Cabarrus, Davidson, Forsyth, Mecklenburg, Rowan, Stokes, Union, and Yadkin.',
    [LOB.BHOM]:
        'Limited to the following counties: Alamance, Caswell, Chatham, Durham, Franklin, Johnston, Lee, Nash, Orange, Person, and Wake.',
    [LOB.ICLT]:
        'Limited to the following counties: Anson, Cabarrus, Cleveland, Davidson, Davie, Forsyth, Gaston, Guilford, Lincoln, Mecklenburg, Randolph, Rowan, Stanly, Stokes, Union, Wilkes, and Yadkin.',
    [LOB.ILOW]:
        'Limited to the following counties: Alexander, Brunswick, Burke, Caldwell, Catawba, Cleveland, Columbus, Duplin, Gaston, Iredell, Lincoln, New Hanover, Onslow, Pender, and Surry.',
};

/**
 * Generates a pharmacy link based on the heatmap network.
 * @param heatmapNetwork - The heatmap network for the plan.
 * @returns The pharmacy link URL or `nothing` if the heatmap network is invalid.
 */
export function getPharmacyLink(heatmapNetwork: string, effectiveStartDate?: string): string {
    if (!heatmapNetwork) return '';

    const pharmacyLinks: { [key: string]: string } = shouldUseLegacyLinks(effectiveStartDate)
        ? {
              Broad: 'https://www.myprime.com/v/BCBSNC/COMMERCIAL/NCHIMESSQ/NCSELECT/en/find-pharmacy.html',
              Limited: 'https://www.myprime.com/v/BCBSNC/COMMERCIAL/NCHIMESSQ/NCLIMITEDCHOICE/en/find-pharmacy.html',
              Local: 'https://www.myprime.com/v/BCBSNC/COMMERCIAL/NCHIMESSQ/NCLIMITEDCHOICE/en/find-pharmacy.html',
          }
        : {
              Broad: 'https://welcome.benefitrx.com/bcbsnc-comm-network3/pharmacy-locator',
              Limited: 'https://welcome.benefitrx.com/bcbsnc-comm-network1/pharmacy-locator',
              Local: 'https://welcome.benefitrx.com/bcbsnc-comm-network1/pharmacy-locator',
          };

    const link = pharmacyLinks[heatmapNetwork];
    return link || '';
}

export function getMedicationLink(planPricing?: string, effectiveStartDate?: string): string {
    const useLegacyMedicationLinks = shouldUseLegacyLinks(effectiveStartDate);
    const drugBaseStandardUrl = useLegacyMedicationLinks
        ? 'https://www.myprime.com/v/BCBSNC/COMMERCIAL/NCHIMQHPS/en/forms.html'
        : 'https://welcome.benefitrx.com/bcbsnc-comm-formulary6/prescription-drug-list';
    const drugBaseUrl = useLegacyMedicationLinks
        ? 'https://www.myprime.com/v/BCBSNC/COMMERCIAL/NCHIMESSQ/en/forms.html'
        : 'https://welcome.benefitrx.com/bcbsnc-comm-formulary5/prescription-drug-list';

    if (planPricing?.toUpperCase() === PRICING.EASY) {
        return drugBaseStandardUrl;
    } else {
        return drugBaseUrl;
    }
}

export function formatInOutValues(plan: BackendPlan, field: string): { inNetwork: string; outOfNetwork: string } {
    if (field === 'coinsuranceText') {
        return {
            inNetwork: `${plan.coinsuranceInn} coinsurance`,
            outOfNetwork: `${plan.coinsuranceOon} coinsurance`,
        };
    } else if (field == 'specialist') {
        return {
            inNetwork: plan.specialistCareInn || plan.specialist.split('/')[0],
            outOfNetwork: plan.specialistCareOon || plan.specialist.split('/')[1],
        };
    }

    // Handle Tier medications dynamically
    const tierFields = {
        rxGeneric: { inKey: 'tier1MedInn', outKey: 'tier1MedOnn' },
        rxGenericNonPreferred: { inKey: 'tier2MedInn', outKey: 'tier2MedOnn' },
        rxBrandPreferred: { inKey: 'tier3MedInn', outKey: 'tier3MedOnn' },
        rxSpecialty: { inKey: 'tier4MedInn', outKey: 'tier4MedOnn' },
        rxBrandNonPreferred: { inKey: 'tier5MedInn', outKey: 'tier5MedOnn' },
    };

    if (field in tierFields) {
        const { inKey, outKey } = tierFields[field as keyof typeof tierFields];
        const inNetwork = String(plan[inKey as keyof BackendPlan] || 'N/A');
        const outOfNetwork = String(plan[outKey as keyof BackendPlan] || 'N/A');
        if (inNetwork === 'N/A' && outOfNetwork === 'N/A') {
            return { inNetwork: "Doesn't Apply", outOfNetwork: "Doesn't Apply" };
        }
        return {
            inNetwork,
            outOfNetwork,
        };
    }

    const inNetworkKey = `${field}Inn` as keyof BackendPlan;
    const outOfNetworkKey = `${field}Oon` as keyof BackendPlan;

    // Check if the keys exist in the plan object
    const inNetwork = String(plan[inNetworkKey] || 'N/A');
    const outOfNetwork = String(plan[outOfNetworkKey] || 'N/A');

    // If both values are "N/A", return "Doesn't Apply"
    if (inNetwork === 'N/A' && outOfNetwork === 'N/A') {
        return { inNetwork: "Doesn't Apply", outOfNetwork: "Doesn't Apply" };
    }

    return {
        inNetwork,
        outOfNetwork,
    };
}

export function enrollPlanHandler(planId: string, qhpId?: string, data?: BackendFormResponse) {
    dispatchEvent(makeCustomEvent(EVENT.SELECT_PLAN, { planId, qhpId, data }));
}

export function keepPlanHandler(planCode: string) {
    const planIshopForm = getIshopForm(ISHOP_FORM_ID.ALT_QUOTE_KEEP);
    if (planIshopForm) {
        planIshopForm.currentPlanBolProductCode.value = planCode;
        planIshopForm.submit();
    } else {
        console.error('Cannot find document form: ', ISHOP_FORM_ID.ALT_QUOTE_KEEP);
    }
}

export function selectNewPlanHandler(planCode: string) {
    const planIshopForm = getIshopForm(ISHOP_FORM_ID.ALT_CHANGE_KEEP);
    if (planIshopForm) {
        planIshopForm.selectedBolProductCode.value = planCode;
        planIshopForm.submit();
    } else {
        console.error('Cannot find document form: ', ISHOP_FORM_ID.ALT_CHANGE_KEEP);
    }
}

export function recommendPlanHandler(planId: string) {
    dispatchEvent(makeCustomEvent(EVENT.RECOMMEND_PLAN, { planId }));
}

export function getPlanRate(plan?: BackendPlan): number {
    if (!plan) return 0;
    return plan?.totalIndividualResponsibilityAmt ?? plan?.planRate?.monthlyResponsibility;
}

export function getPlanName(plan?: BackendPlan): string {
    if (!plan) return '';
    return plan?.name ?? plan?.marketingName;
}
